import type {
  AgentActionStatus,
  AgentBlock,
  AgentChartKind,
  AgentEvent,
  AgentPendingAction,
  AgentTurnStatus,
} from './types';

const TURN_STATUSES: readonly AgentTurnStatus[] = [
  'completed',
  'awaiting_confirmation',
  'step_limit',
  'stopped',
  'failed',
];

const ACTION_STATUSES: readonly AgentActionStatus[] = [
  'pending',
  'applied',
  'cancelled',
  'failed',
  'stale',
  'expired',
];

const CHART_KINDS: readonly AgentChartKind[] = ['line', 'bar', 'pie', 'table'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function str(value: unknown): value is string {
  return typeof value === 'string';
}

export function action(raw: unknown): AgentPendingAction | null {
  if (!isRecord(raw)) return null;
  if (!str(raw.id) || !str(raw.tool) || !str(raw.title) || !str(raw.expires_at)) return null;
  if (!ACTION_STATUSES.includes(raw.status as AgentActionStatus)) return null;
  return {
    id: raw.id,
    tool: raw.tool,
    title: raw.title,
    destructive: raw.destructive === true,
    arguments: isRecord(raw.arguments) ? raw.arguments : {},
    before: isRecord(raw.before) ? raw.before : null,
    status: raw.status as AgentActionStatus,
    expires_at: raw.expires_at,
  };
}

function actions(raw: unknown): AgentPendingAction[] | null {
  if (!Array.isArray(raw)) return null;
  const parsed = raw.map(action).filter((one): one is AgentPendingAction => one !== null);
  return parsed.length === raw.length ? parsed : null;
}

function options(raw: unknown): string[] | null {
  if (!Array.isArray(raw) || raw.length === 0 || !raw.every(str)) return null;
  return raw as string[];
}

/** A chart's payload, shared by the live event and the stored block. */
function chart(raw: Record<string, unknown>): (AgentBlock & { type: 'chart' }) | null {
  if (!str(raw.chart_id) || !str(raw.x_key)) return null;
  if (!CHART_KINDS.includes(raw.kind as AgentChartKind)) return null;
  const yKeys = raw.y_keys;
  if (!Array.isArray(yKeys) || yKeys.length === 0 || !yKeys.every(str)) return null;
  if (
    !Array.isArray(raw.rows) ||
    !raw.rows.every(
      (row) => isRecord(row) && Object.values(row).every((v) => typeof v === 'string' || typeof v === 'number'),
    )
  ) {
    return null;
  }
  return {
    type: 'chart',
    chart_id: raw.chart_id,
    kind: raw.kind as AgentChartKind,
    title: str(raw.title) ? raw.title : '',
    x_key: raw.x_key,
    y_keys: yKeys as string[],
    rows: raw.rows as Record<string, string | number>[],
  };
}

/**
 * One wire frame as a typed event, or `null`.
 *
 * Returning `null` rather than throwing is the point: a backend that grows a
 * seventh event type must not break a tab that is still running yesterday's
 * bundle, so anything unrecognised is dropped and the turn carries on.
 */
export function parseAgentEvent(raw: unknown): AgentEvent | null {
  if (!isRecord(raw) || !str(raw.type)) return null;

  switch (raw.type) {
    case 'turn_started':
      return str(raw.turn_id) && str(raw.message_id)
        ? { type: 'turn_started', turn_id: raw.turn_id, message_id: raw.message_id }
        : null;
    case 'text_delta':
      return str(raw.text) ? { type: 'text_delta', text: raw.text } : null;
    case 'tool_started':
      return str(raw.tool_use_id) && str(raw.tool) && str(raw.title)
        ? { type: 'tool_started', tool_use_id: raw.tool_use_id, tool: raw.tool, title: raw.title }
        : null;
    case 'tool_finished':
      return str(raw.tool_use_id) && typeof raw.ok === 'boolean' && str(raw.summary)
        ? { type: 'tool_finished', tool_use_id: raw.tool_use_id, ok: raw.ok, summary: raw.summary }
        : null;
    case 'chart':
      return chart(raw);
    case 'suggestions': {
      const parsed = options(raw.options);
      return parsed ? { type: 'suggestions', options: parsed } : null;
    }
    case 'confirmation_required': {
      const parsed = actions(raw.actions);
      return parsed ? { type: 'confirmation_required', actions: parsed } : null;
    }
    case 'action_resolved':
      return str(raw.id) && str(raw.summary) && ACTION_STATUSES.includes(raw.status as AgentActionStatus)
        ? {
            type: 'action_resolved',
            id: raw.id,
            status: raw.status as AgentActionStatus,
            summary: raw.summary,
          }
        : null;
    case 'turn_done': {
      if (!TURN_STATUSES.includes(raw.status as AgentTurnStatus)) return null;
      const usage = raw.usage;
      return {
        type: 'turn_done',
        status: raw.status as AgentTurnStatus,
        usage: isRecord(usage)
          ? {
              input_tokens: Number(usage.input_tokens) || 0,
              output_tokens: Number(usage.output_tokens) || 0,
              cache_read_tokens: Number(usage.cache_read_tokens) || 0,
              cache_write_tokens: Number(usage.cache_write_tokens) || 0,
            }
          : null,
      };
    }
    case 'error':
      return str(raw.code) && str(raw.message)
        ? { type: 'error', code: raw.code, message: raw.message, detail: raw.detail }
        : null;
    default:
      return null;
  }
}

/** One stored block from `GET /agent/conversations/{id}`, or `null`. */
export function parseAgentBlock(raw: unknown): AgentBlock | null {
  if (!isRecord(raw) || !str(raw.type)) return null;

  switch (raw.type) {
    case 'user_text':
      return str(raw.text) ? { type: 'user_text', text: raw.text } : null;
    case 'assistant_text':
      return str(raw.text) ? { type: 'assistant_text', text: raw.text } : null;
    case 'tool_activity':
      return str(raw.tool_use_id) && typeof raw.ok === 'boolean' && str(raw.summary)
        ? {
            type: 'tool_activity',
            tool_use_id: raw.tool_use_id,
            tool: str(raw.tool) ? raw.tool : '',
            ok: raw.ok,
            summary: raw.summary,
          }
        : null;
    case 'chart':
      return chart(raw);
    case 'suggestions': {
      const parsed = options(raw.options);
      return parsed ? { type: 'suggestions', options: parsed } : null;
    }
    case 'confirmation': {
      const parsed = actions(raw.actions);
      return parsed ? { type: 'confirmation', actions: parsed } : null;
    }
    default:
      return null;
  }
}

/**
 * A server timestamp as a `Date`, or `null`.
 *
 * The backend sends `expires_at` in two forms: the replay serializes through
 * Pydantic (`2026-09-18T05:29:00.135560Z`) while the live stream serializes
 * through `json.dumps(default=str)`, which gives Python's `str(datetime)` —
 * `2026-09-18 05:28:55.522609+00:00`, with a space where ISO 8601 wants a `T`.
 * ECMA-262 only requires engines to parse the ISO form, so the space one is
 * normalized here rather than trusted to browser goodwill.
 */
export function parseAgentTimestamp(value: string | null | undefined): Date | null {
  if (!value) return null;
  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}
